/**
 * Local Pickup Page Entrypoint
 *
 * Main entry point for the local pickup page.
 * Loads map, schedule, and cart Web Components, initializes stores,
 * and sets up the integration with legacy data sources.
 *
 * Components registered:
 * - <peach-local-pickup-view> - Page orchestrator with map/schedule toggle
 * - <peach-map-view> - Map view orchestrator
 * - <peach-schedule-view> - Schedule view orchestrator
 * - <peach-map-canvas> - Google Maps integration
 * - <peach-locations-list> - Scrollable location list
 * - <peach-location-details> - Location details panel
 * - <peach-cart-drawer> - Cart drawer (shared)
 *
 * Usage in Liquid:
 * ```liquid
 * {% render 'vite-tag', entry: 'page-map.ts' %}
 * ```
 */

// Import Tailwind CSS
import "./tailwind.css";

// Import cart components (global)
import "@/features/cart";

// Import local-pickup orchestrator (includes map + schedule features)
import "@/features/local-pickup";

// Import stores
import { cartStore } from "@/features/cart/stores/cart-store";
import { locationStore } from "@/features/locations/stores/location-store";
import { mapViewStore } from "@/features/map/stores/map-view-store";
import { uiStore } from "@/stores/ui-store";

// Import URL state service
import { urlState } from "@/services/url-state";

// Import Jawg Places service for zipcode search
import { jawgPlaces } from "@/services/jawg-places";

// Shared name-search behavior (also used by the search box on Enter)
import { applyNameSearch } from "@/features/map/utils/apply-name-search";

// Import IP geolocation service
import { getLocationFromIp } from "@/services/ip-geolocation";

// Ensure window types are available
import type {} from "@/types/window";

/**
 * Initialize stores with configuration from Liquid
 */
function initializeStores() {
  // Look for configuration passed from Liquid via data attributes or JSON script tag
  const mapConfig = document.getElementById("map-config");

  if (mapConfig) {
    try {
      const config = JSON.parse(mapConfig.textContent || "{}");

      // Initialize location store with data URL
      if (config.locationsUrl) {
        console.log("[Map] Loading locations from:", config.locationsUrl);
        locationStore.getState().setLocationsUrl(config.locationsUrl);
      }

      // Set any initial filter values
      if (config.initialFilters) {
        locationStore.getState().updateFilters(config.initialFilters);
      }

      // Store other config for components to access
      if (config.googleMapsApiKey) {
        (window as any).__MAP_CONFIG__ = config;
      }
    } catch (error) {
      console.error("[Map] Failed to parse map config:", error);
    }
  } else {
    // Fallback: read from legacy global variables if they exist
    const legacyUrl = (window as any).locations_url;
    if (legacyUrl) {
      console.log("[Map] Using legacy locations_url:", legacyUrl);
      locationStore.getState().setLocationsUrl(legacyUrl);
    }
  }

  // Initialize cart on page load
  cartStore.getState().fetchCart();
}

/**
 * Handle location search on page load
 *
 * Searches for the location (zipcode, city, or address) using Jawg Places API,
 * centers the map on the result, and applies proximity filtering.
 *
 * @param searchQuery - The search query (zipcode, city, or address)
 */
async function handleLocationSearch(searchQuery: string) {
  // A query that matches pickup locations by name (e.g. "Lowe's") runs a name
  // search across all matches, not a single geocoded point. This keeps a
  // refreshed `?search=` URL consistent with pressing Enter in the search box.
  // Only fall back to Jawg geocoding when nothing matches by name (a
  // locality/zip/address such as "Saratoga Springs").
  //
  // On URL restore this runs after restoreFromUrl(), and applyNameSearch clears
  // any `loc`/`slot` selection. A `?search=` query therefore takes precedence
  // over a specific stop in the same URL — deliberately, matching the search box
  // (a new search there also clears loc/slot). A shared link that should open one
  // stop must omit `search`.
  if (applyNameSearch(searchQuery)) {
    return;
  }

  try {
    console.log("[Map] Initiating location search for:", searchQuery);

    // Search using Jawg Places API
    const results = await jawgPlaces.autocomplete({
      text: searchQuery,
      layers: ["postalcode", "locality", "county"], // Support zipcode, city, and county
    });

    if (results.length === 0) {
      console.warn("[Map] No results found for search:", searchQuery);
      return;
    }

    // Use the first result
    const result = results[0];
    console.log("[Map] Found location:", result.label, result);

    // IMPORTANT: Set mapCenter FIRST before changing visible locations
    // This ensures the shouldSkipFitBounds flag is set before updateMarkers() runs
    mapViewStore.getState().setMapCenter({
      lat: result.lat,
      lng: result.lng,
      zoom: 10,
    });

    // Update filters (persists searchQuery to localStorage)
    locationStore.getState().updateFilters({
      searchQuery: result.label,
    });

    // Set search point for proximity filtering (persists to localStorage)
    // This changes visible locations, which triggers updateMarkers()
    // But mapCenter is already set, so fitBounds() will be skipped
    locationStore.getState().setSearchPoint({
      lat: result.lat,
      lng: result.lng,
      radiusMiles: 50,
    });

    console.log("[Map] Map centered and filtered by location:", result.label);
  } catch (error) {
    console.error("[Map] Error searching for location:", error);
  }
}

/**
 * Center the map on a location specified in the URL `loc` parameter.
 *
 * @remarks
 * Called when the URL contains a `loc` param but no `search` param.
 * This ensures shared links like `?loc=1924` display the correct location
 * and filter the list to nearby locations, rather than falling back to
 * IP geolocation or a stale localStorage search.
 */
function centerOnUrlLocation() {
  const urlParams = urlState.getState();
  if (!urlParams.loc) return;

  const allLocations = locationStore.getState().getAllLocations();
  const location = allLocations.find((loc) => loc.id === urlParams.loc);

  if (!location) return;

  mapViewStore.getState().setMapCenter({
    lat: location.lat,
    lng: location.lng,
    zoom: 11,
  });

  locationStore.getState().setSearchPoint({
    lat: location.lat,
    lng: location.lng,
    radiusMiles: 50,
  });
}

/**
 * Determine and apply a default map center for first-time visitors.
 *
 * @remarks
 * Skipped when URL contains navigation context (loc or search params).
 * This ensures IP geolocation only runs on fresh visits without URL params.
 * If IP geolocation fails, focuses the search input to prompt user action.
 */
async function applyDefaultMapCenter() {
  const urlParams = urlState.getState();
  const hasUrlNavigationContext = !!(urlParams.loc || urlParams.search);

  if (hasUrlNavigationContext) return;

  console.log("[Map] No stored location context - attempting IP geolocation");

  const ipLocation = await getLocationFromIp();

  if (ipLocation) {
    console.log(
      "[Map] IP geolocation succeeded:",
      ipLocation.city,
      ipLocation.region,
    );

    // Center map on user's location
    mapViewStore.getState().setMapCenter({
      lat: ipLocation.lat,
      lng: ipLocation.lng,
      zoom: 11,
    });

    // Filter locations by proximity (50 mile radius)
    // This prevents showing all 362 locations on initial load
    locationStore.getState().setSearchPoint({
      lat: ipLocation.lat,
      lng: ipLocation.lng,
      radiusMiles: 50,
    });

    return;
  }

  console.log("[Map] IP geolocation unavailable - focusing search input");
  // Focus the search input to prompt user to enter location
  const searchInput = document.querySelector<HTMLInputElement>(
    'input[type="search"], input[placeholder*="city"], input[placeholder*="zip"]'
  );
  if (searchInput) {
    searchInput.focus();
  }
}

/**
 * Set up URL-based navigation and state restoration
 *
 * Handles:
 * - Restoring state from URL on page load
 * - Syncing state changes to URL
 * - Browser back/forward navigation
 */
function setupUrlNavigation() {
  // Flag to prevent circular updates between store and URL
  let isRestoringFromUrl = false;

  // Restore state from URL after locations data is loaded into locationStore
  // We wait for locationStore.getAllLocations() to be populated before restoring URL state.
  const unsubscribeLocationData = locationStore.subscribe(
    (state) => ({ tours: state.tours, markets: state.markets }),
    (_selected) => {
      // Wait until locations are loaded into locationStore
      const allLocations = locationStore.getState().getAllLocations();
      if (allLocations.length === 0) {
        console.log("[Map] Waiting for locations to be loaded into locationStore...");
        return;
      }

      // Only restore once
      if (!isRestoringFromUrl) {
        isRestoringFromUrl = true;

        console.log(
          "[Map] Restoring state from URL (locations available:",
          allLocations.length,
          ")",
        );

        // Restore UI state first (cart drawer, etc.)
        // This must happen before search so that drawer states are set before map centering
        uiStore.getState().restoreFromUrl();

        // Restore location state (location, date, stop selections)
        locationStore.getState().restoreFromUrl();

        // Check for search parameter and initiate search
        // This happens AFTER UI state restoration so drawer offsets are correct
        const urlParams = urlState.getState();
        if (urlParams.search) {
          console.log("[Map] Search parameter detected:", urlParams.search);
          handleLocationSearch(urlParams.search);
        } else if (urlParams.loc) {
          // Center on URL location when loc is present but no search
          // This ensures shared links display the correct location area
          centerOnUrlLocation();
        }

        isRestoringFromUrl = false;

        // Unsubscribe after first restoration
        unsubscribeLocationData();

        // Apply IP-based default center when no context is available.
        // Runs after URL restoration so stored/URL search params take precedence.
        applyDefaultMapCenter();
      }
    },
    {
      // Fire immediately to check if locations are already loaded
      fireImmediately: true,
    },
  );

  // Sync location state changes to URL (debounced)
  locationStore.subscribe(
    (state) => ({
      selectedLocationId: state.selectedLocationId,
      selectedDate: state.selectedDate,
      selectedStopId: state.selectedStopId,
    }),
    () => {
      // Skip if we're currently restoring from URL
      if (isRestoringFromUrl) {
        return;
      }

      locationStore.getState().syncToUrl();
    },
    {
      // Fire immediately on subscription and on changes
      fireImmediately: false,
    },
  );

  // Sync UI state changes to URL (debounced)
  uiStore.subscribe(
    (state) => state.cartOpen,
    () => {
      // Skip if we're currently restoring from URL
      if (isRestoringFromUrl) {
        return;
      }

      uiStore.getState().syncToUrl();
    },
    {
      fireImmediately: false,
    },
  );

  // Handle browser back/forward navigation
  urlState.onPopState((urlParams) => {
    console.log("[Map] Browser navigation detected:", urlParams);

    isRestoringFromUrl = true;

    // Restore location state from URL
    locationStore.getState().restoreFromUrl();

    // Restore UI state from URL
    uiStore.getState().restoreFromUrl();

    isRestoringFromUrl = false;
  });

  console.log("[Map] URL navigation setup complete");
}

/**
 * Set up global event listeners
 */
function setupEventListeners() {
  // No global listeners needed - components handle their own events
}

/**
 * Initialize the map page
 */
function init() {
  console.log("[Map] Initializing map page components");

  initializeStores();
  setupEventListeners();
  setupUrlNavigation();

  console.log("[Map] Map page initialized");
  console.log("[Map] - Cart Store:", cartStore.getState());
  console.log("[Map] - Location Store:", locationStore.getState());
}

// Run initialization when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

/**
 * Handle bfcache restoration
 *
 * @remarks
 * When the browser restores this page from back/forward cache (bfcache),
 * the component state and stores are corrupted. Force a clean reload.
 * This is the standard approach for complex map-based applications.
 */
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    console.log("[Map] Page restored from bfcache - reloading");
    window.location.reload();
  }
});

// Export for debugging in console (dev/staging only, tree-shaken in production)
if (import.meta.env.MODE !== "production") {
  (window as any).__MAP_STORES__ = {
    cart: cartStore,
    location: locationStore,
    mapView: mapViewStore,
    ui: uiStore,
  };
}

// Dev-only: Auto-inject font theme switcher for testing
if (import.meta.env.DEV) {
  console.log("[Map] Loading theme-switcher (dev mode)...");
  import("@/features/devtools/theme-switcher")
    .then(() => {
      console.log("[Map] Theme-switcher loaded, creating element...");
      const switcher = document.createElement("theme-switcher");
      document.body.appendChild(switcher);
      console.log("[Map] Theme-switcher appended to body");
    })
    .catch((error) => {
      console.error("[Map] Failed to load theme-switcher:", error);
    });
}

console.log("[Vite] Map page components loaded");
